import React, { useRef, useState, useMemo } from 'react';
import { Box, Stack, HStack, Text, Input, Button, VStack, useToast, Image } from '@chakra-ui/react';
import { FaEdit, FaTrash, FaCheck, FaPlus } from 'react-icons/fa';
import axios from 'axios';
import { MultiSelectOption, DependentQuestionMultiSelect } from '../GenericMultiSelectFilter';
import { CustomDropdownWithSearchBar } from './CustomDropdownWithSearchBar';
import { CustomButton, CustomInput, CustomText } from '../../atoms';
import { ToggleAtom } from '../../atoms/CustomToggle';
import { FormField, FormFieldOptions, useSettings } from '../../../stateManagement/SettingsContext';
import { updateConfigVersion } from '../../../utils';
import { DeleteConfirmation } from './DeletionConfirmationMolecule';

export interface FormFieldOption {
  question?: string[];
  showIf?: string[];
}

interface CifData {
  data: { key: string; label: string; type: string; data?: { options?: any[] } }[];
  is_agent: boolean;
}

interface AvailableDependentQuestion {
  id: string;
  question: string;
}

interface CifSettingsOrganismProps {
  onFormCardChange: (newCards: FormField[]) => void;
  cifs: CifData;
  contactUsFormData: FormField[];
}

export const ContactUsSettingsMolecule: React.FC<CifSettingsOrganismProps> = ({
  onFormCardChange,
  cifs,
  contactUsFormData,
}) => {
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [currentEdit, setCurrentEdit] = useState<FormField | null>(null);
  const [selectedCif, setSelectedCif] = useState<string>('');
  const [selectedCifKey, setSelectedCifKey] = useState<string>('');
  const [isRequired, setIsRequired] = useState(false);
  const [tempOptionsConfig, setTempOptionsConfig] = useState<FormFieldOptions>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [allDependentFields, setAllDependentFields] = useState<AvailableDependentQuestion[]>([]);
  const [availableCifs, setAvailableCifs] = useState<CifData>({
    data: cifs.data.filter(
      cif => !contactUsFormData.some(field => field.linked_cif_key === cif.key),
    ),
    is_agent: cifs.is_agent,
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { adminApiKey, globalConfigData } = useSettings();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Helper to find all IDs currently linked by any dropdown in the form
  const getAllLinkedIds = (formData: FormField[], excludeCardId: string): string[] => {
    let linkedIds: string[] = [];
    formData.forEach(card => {
      // Exclude the card currently being edited from the global check
      if (card.id === excludeCardId) return;

      if (card.type === 'dropdown' && card.options) {
        Object.values(card.options).forEach(option => {
          linkedIds = [...linkedIds, ...(option.question || [])];
        });
      }
    });
    // Ensure uniqueness
    return Array.from(new Set(linkedIds));
  };

  const allFormDependentFields = useMemo(() => {
    // Hardcode the ID to exclude from the dependent question pool
    const ID_TO_EXCLUDE = 'q3';

    return (contactUsFormData ?? [])
      .filter(f => f.dependent && f.id !== ID_TO_EXCLUDE)
      .map(f => ({ id: f.id, question: f.question }));
  }, [contactUsFormData]);

  const handleEditMenuCard = (card: FormField) => {
    setEditingCardId(card.id);
    // Deep copy options for isolated editing
    setCurrentEdit({
      ...card,
      options: card.options ? JSON.parse(JSON.stringify(card.options)) : undefined,
    });

    setIsRequired(card.required);
    setSelectedCif(cifs.data.find(cif => cif.key === card.linked_cif_key)?.label || '');
    setSelectedCifKey(card.linked_cif_key || '');

    if (card.type === 'dropdown' && card.dependent) {
      setTempOptionsConfig(card.options || {});

      // 1. Get ALL IDs linked by *other* dropdowns in the form
      const globallyLinkedIds = getAllLinkedIds(contactUsFormData, card.id);

      // 2. Filter the master list (allFormDependentFields) against the global linked pool
      const unlinkedDependentFields = allFormDependentFields
        // Filter out questions linked globally by other cards
        .filter(f => !globallyLinkedIds.includes(f.id));

      setAllDependentFields(unlinkedDependentFields);
    } else {
      setTempOptionsConfig({});
      setAllDependentFields([]);
    }
  };

  const handleLinkedQuestionsSelect = (optionLabel: string, selectedIds: string[]) => {
    setTempOptionsConfig(prevConfig => ({
      ...prevConfig,
      [optionLabel]: {
        ...prevConfig[optionLabel],
        question: selectedIds,
      },
    }));
  };

  const handleShowIfChange = (optionLabel: string, value: string) => {
    // Input is comma-separated string, output must be array of strings
    const newShowIfConditions = value
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    setTempOptionsConfig(prevConfig => ({
      ...prevConfig,
      [optionLabel]: {
        ...prevConfig[optionLabel],
        showIf: newShowIfConditions,
      },
    }));
  };

  const handleSaveEdit = () => {
    if (!currentEdit || !editingCardId) {
      return;
    }

    let updatedCards: FormField[] = contactUsFormData ?? [];
    let updatedEdit = {
      ...currentEdit,
    };

    // Step 1: Finalize the currentEdit object by merging temporary options config
    if (currentEdit.type === 'dropdown' && currentEdit.dependent) {
      // 1. Establish baseOptions using ALL linked_cif_options
      const baseOptions = Array.isArray(currentEdit.linked_cif_options)
        ? currentEdit.linked_cif_options.reduce((acc: FormFieldOptions, curr) => {
            // Ensures ALL CIF option labels become keys in baseOptions
            return {
              ...acc,
              [curr.label]: currentEdit.options?.[curr.label] || {},
            };
          }, {})
        : currentEdit.options || {};

      const mergedOptions: FormFieldOptions = {};

      // 2. Iterate through all base keys and merge/overwrite with tempOptionsConfig
      Object.keys(baseOptions).forEach(label => {
        mergedOptions[label] = {
          // Keep existing structure (if any)
          ...baseOptions[label],
          // Overwrite with changes made in the dependency UI (tempOptionsConfig)
          ...tempOptionsConfig[label],
        };
      });

      updatedEdit = { ...updatedEdit, options: mergedOptions };
    }

    // Step 2: Update the form cards array
    updatedCards = updatedCards.map(card => (card.id === editingCardId ? updatedEdit : card));

    onFormCardChange(updatedCards);
    setEditingCardId(null);
    setCurrentEdit(null);
    setSelectedCif('');
    setSelectedCifKey('');

    // Cleanup dependency state
    setTempOptionsConfig({});
    setAllDependentFields([]);
  };

  const cifDropdownItems = availableCifs.data.map(item => ({
    label: item.label,
    value: item.key,
  }));

  const handleAddFormCard = () => {
    const newCard: FormField = {
      id: `q${Date.now().toString()}`,
      question: '',
      description: '',
      placeholder: '',
      required: false,
      type: 'singleline',
    };
    // onFormCardChange([...contactUsFormData.slice(0, 2), newCard, ...contactUsFormData.slice(2)]);
    onFormCardChange([...contactUsFormData, newCard]);
    setEditingCardId(newCard.id);
    setCurrentEdit(newCard);
    setSelectedCif('');
    setSelectedCifKey('');
    setTempOptionsConfig({});
    setAllDependentFields([]);
  };
  // Add this helper function inside ContactUsSettingsMolecule
  const getDependentQuestionParentId = (questionId: string): string | undefined => {
    // We iterate over the entire form array to find the card that links the given ID
    const parentCard = contactUsFormData.find(card => {
      if (card.type !== 'dropdown' || !card.options) return false;

      // Check if the questionId is present in any option's 'question' array
      return Object.values(card.options).some(
        option => option.question && option.question.includes(questionId),
      );
    });
    return parentCard?.id;
  };

  const handleDeleteMenuCard = (id: string) => {
    let updatedCards: FormField[] = contactUsFormData ?? [];

    // Clean up nesting links from all parent dropdowns
    updatedCards = updatedCards.map(card => {
      // Check if the current card is a dropdown and has options to manage
      if (card.type === 'dropdown' && card.options) {
        const originalOptions = card.options;
        const newOptions: FormFieldOptions = {};
        let optionsChanged = false;

        // Iterate over every option label in this dropdown
        Object.keys(originalOptions).forEach(optionLabel => {
          // Access the specific option safely
          const option: FormFieldOption = originalOptions[optionLabel];

          // Check if the 'question' array links to the ID being deleted
          if (option?.question?.includes(id)) {
            optionsChanged = true;

            // Create a new 'question' array excluding the deleted ID
            const newQuestionArray = (option.question || []).filter(linkedId => linkedId !== id);

            // Rebuild the option object with the new array
            newOptions[optionLabel] = {
              ...option,
              question: newQuestionArray,
            };
          } else {
            // If the option didn't link the deleted ID, keep it as is
            newOptions[optionLabel] = option;
          }
        });

        // If changes were made to the options, return the updated card
        if (optionsChanged) {
          return { ...card, options: newOptions };
        }
      }

      // Return card unchanged if it's not a dropdown or doesn't link the deleted ID
      return card;
    });

    // Delete the target card itself ---
    const finalCards: FormField[] = updatedCards.filter(card => card.id !== id);

    onFormCardChange(finalCards);

    setAvailableCifs(prev => ({
      ...prev,
      data: [
        ...(selectedCifKey ? cifs.data.filter(cif => cif.key === selectedCifKey) : []),
        ...prev.data.filter(cif => cif.key !== selectedCifKey),
      ],
    }));
    setSelectedCif('');
    setSelectedCifKey('');
  };

  const handleQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (currentEdit) {
      setCurrentEdit({ ...currentEdit, question: event.target.value });
    }
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (currentEdit) {
      setCurrentEdit({ ...currentEdit, description: event.target.value });
    }
  };

  const handlePlaceholderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (currentEdit) {
      setCurrentEdit({ ...currentEdit, placeholder: event.target.value });
    }
  };

  const handleToggleChange = (value: boolean) => {
    if (currentEdit) {
      setCurrentEdit({ ...currentEdit, required: value });
      setIsRequired(value);
    }
  };

  const handleCifDropdownItemClick = (value: string) => {
    const selectedItem = cifs.data.find(item => item.key === value);

    setAvailableCifs(prev => ({
      ...prev,
      data: prev.data.filter(cif => cif.key !== selectedItem?.key),
    }));

    setSelectedCif(selectedItem?.label || '');
    setSelectedCifKey(selectedItem?.key || '');

    const linkedCifOptions = selectedItem?.data?.options || [];

    if (currentEdit) {
      const validTypes = [
        'number',
        'checkbox',
        'heading',
        'radio',
        'tel',
        'url',
        'email',
        'date',
        'singleline',
        'multiline',
        'dropdown',
        'information',
        'password',
        'file',
      ];
      const newType = (
        validTypes.includes(selectedItem?.type ?? '') ? selectedItem?.type : 'singleline'
      ) as FormField['type'];
      const isDropdown = newType === 'dropdown';

      // Calculate the options object outside of setCurrentEdit
      let calculatedOptions: FormFieldOptions = {};

      if (isDropdown && Array.isArray(linkedCifOptions)) {
        calculatedOptions = linkedCifOptions.reduce((acc: any, curr: any) => {
          // Using immutable approach (as fixed previously)
          return {
            ...acc,
            [curr.label]: currentEdit.options?.[curr.label] || {},
          };
        }, {});
      } else if (!isDropdown) {
        // If it's not a dropdown, options must be an empty object
        calculatedOptions = {};
      } else {
        // If isDropdown is true but linkedCifOptions is not an array (e.g., null/undefined), use empty object
        calculatedOptions = {};
      }

      setCurrentEdit({
        ...currentEdit,
        linked_cif_key: selectedItem?.key,
        linked_cif_options: linkedCifOptions,
        type: newType,
        options: calculatedOptions,
        dependent: currentEdit.dependent && isDropdown,
        // reset flag if not multiline
        isUserFirstMessage:
          newType === 'multiline' ? currentEdit.isUserFirstMessage || false : false,
      });

      // Cleanup/Setup dependency state
      setTempOptionsConfig(calculatedOptions); // Use the calculated variable
      if (isDropdown && currentEdit.dependent) {
        const availableOptions = allFormDependentFields.filter(f => f.id !== currentEdit.id);
        setAllDependentFields(availableOptions);
      } else {
        setAllDependentFields([]);
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) return;
      if (file.size > 5 * 1024 * 1024) return;
      const renamedFile = new File([file], 'banner.png', { type: file.type });
      setSelectedFile(renamedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(renamedFile);
    }
  };

  const convertImageToBase64 = (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result); // Base64 encoded string or ArrayBuffer
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  const BLOB_CONTAINER_NAME = process.env.REACT_APP_BLOB_CONTAINER_NAME || '';

  const toast = useToast();
  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    try {
      const base64String = await convertImageToBase64(selectedFile);
      const timestamp = Date.now();
      const cdnurl = process.env.REACT_APP_CDN_URL;

      // Fetch and update config
      const configRes = await fetch(`${cdnurl}/config.json`);
      const configData = await configRes.json();

      const rawUrl = configData?.configs?.contactUsLogo;

      if (rawUrl) {
        // Remove existing versioning (any v=... in the query params)
        const [urlBase, queryString] = rawUrl.split('?');
        let newQuery = '';

        if (queryString) {
          const params = new URLSearchParams(queryString);
          params.delete('v'); // Remove any existing v param
          newQuery = params.toString();
        }

        // Rebuild final URL with fresh timestamp version
        const versionedUrl =
          newQuery.length > 0
            ? `${urlBase}?${newQuery}&v=${timestamp}`
            : `${urlBase}?v=${timestamp}`;

        configData.configs.contactUsLogo = versionedUrl;
      } else {
        throw new Error('contactUsLogo is missing in configData');
      }

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        // url: 'https://catalysthc.ms.helpshift.live/api/fe-config',
        url: 'https://demo-hc-api.ms.helpshift.live/api/fe-config',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${adminApiKey ?? ''}`,
        },
        data: JSON.stringify([
          {
            name: 'contactUsLogo.svg',
            type: 'image',
            path: `${BLOB_CONTAINER_NAME}/images`,
            data: base64String,
          },
          {
            name: 'config.json',
            type: 'json',
            path: BLOB_CONTAINER_NAME,
            data: JSON.stringify(configData, null, 2),
          },
          {
            name: 'globalConfig.json',
            type: 'json',
            path: `${BLOB_CONTAINER_NAME}`,
            data: JSON.stringify(updateConfigVersion(globalConfigData ?? undefined)),
          },
        ]),
      };
      const response = await axios.request(config);
      if (response.status >= 200 && response.status < 300) {
        toast({
          title: 'Upload successful!',
          description: 'Contact Us Banner Image has been updated.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error during upload:', error);
      toast({
        title: 'Upload failed',
        description: 'An error occurred while uploading image or config.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box height="86.5vh" overflowY="auto" border="1px solid #e2e8f0" p={4} borderRadius="md">
      <CustomButton icon={<FaPlus />} onClick={handleAddFormCard} colorScheme="green">
        Add Form Field
      </CustomButton>
      <Stack spacing={4}>
        <Box mt={6}>
          {[...contactUsFormData]
            .sort((a, b) => {
              // If we are currently editing 'a', prioritize it (-1 means 'a' comes before 'b')
              if (a.id === editingCardId) return -1;
              // If we are currently editing 'b', de-prioritize 'a' (1 means 'b' comes before 'a')
              if (b.id === editingCardId) return 1;

              // If neither is being edited, maintain the original order based on index.
              return contactUsFormData.indexOf(a) - contactUsFormData.indexOf(b);
            })
            .map(card => {
              // Helper variables (Option 3: Explicit return)
              const isStaticContent = card.type === 'heading' || card.type === 'information';
              const isFixedInput = card.id === 'q1' || card.id === 'q2' || card.id === '6';
              const isInputOrDescription = card.type !== 'heading' && card.type !== 'information';

              const ActionButtons =
                editingCardId === card.id ? (
                  <HStack>
                    <CustomButton
                      icon={<FaCheck />}
                      aria-label="Save Changes"
                      onClick={handleSaveEdit}
                      colorScheme="green"
                      isDisabled={!selectedCifKey && !isFixedInput && !isStaticContent}
                    />
                    {!(isStaticContent || isFixedInput) && (
                      <DeleteConfirmation
                        message="Are you sure you want to delete this form field?"
                        onConfirm={() => handleDeleteMenuCard(card.id)}
                        trigger={
                          <CustomButton
                            icon={<FaTrash />}
                            aria-label="Delete Form Field"
                            colorScheme="red"
                          />
                        }
                      />
                    )}
                  </HStack>
                ) : (
                  <CustomButton
                    icon={<FaEdit />}
                    aria-label="Edit Menu Card"
                    onClick={() => handleEditMenuCard(card)}
                    colorScheme="yellow"
                  />
                );

              return (
                <Box
                  key={card.id}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  mb={4}
                  bg={editingCardId === card.id ? 'white' : 'gray.50'}
                  width="100%"
                >
                  {editingCardId === card.id ? (
                    <Stack spacing={3} width="100%" bg="white">
                      {!(isStaticContent || isFixedInput) ? (
                        // If it's a configurable field, show the CIF dropdown and buttons aligned
                        <HStack width="100%" justifyContent="space-between" alignItems="flex-start">
                          <HStack>
                            <CustomDropdownWithSearchBar
                              value={selectedCifKey}
                              label={
                                selectedCif
                                  ? cifs.data.find(item => item.key === selectedCifKey)?.label ||
                                    'Select an option'
                                  : 'Select Cif To Link'
                              }
                              items={cifDropdownItems}
                              onItemClick={handleCifDropdownItemClick}
                              variant="solid"
                            />
                            {!selectedCif && <Text color="red.500">required field *</Text>}
                          </HStack>
                          {ActionButtons}
                        </HStack>
                      ) : (
                        // If it's static content (q1, info, heading), just show the edit button on the right
                        <HStack width="100%" justifyContent="flex-end">
                          {ActionButtons}
                        </HStack>
                      )}

                      {/* QUESTION INPUT */}
                      <HStack width="100%">
                        <CustomInput
                          value={currentEdit?.question || ''}
                          onChange={handleQuestionChange}
                          placeholder="Question"
                          overflow="auto"
                          width="100%"
                        />
                      </HStack>

                      {/* DESCRIPTION INPUT */}
                      <HStack width="100%">
                        <CustomInput
                          value={currentEdit?.description || ''}
                          onChange={handleDescriptionChange}
                          placeholder="Description"
                          overflow="auto"
                          width="100%"
                        />
                      </HStack>

                      {/* PLACEHOLDER INPUT */}
                      {isInputOrDescription && (
                        <HStack width="100%">
                          <CustomInput
                            value={currentEdit?.placeholder || ''}
                            onChange={handlePlaceholderChange}
                            placeholder="Placeholder"
                            overflow="auto"
                            width="100%"
                          />
                        </HStack>
                      )}

                      {/* REQUIRED TOGGLE */}
                      {isInputOrDescription && (
                        <HStack width="100%">
                          <ToggleAtom
                            value={isRequired || false}
                            justifyContent="space-between"
                            onChange={value => {
                              setIsRequired(value);
                              handleToggleChange(value);
                            }}
                            label="Is this field mandatory?"
                          />
                        </HStack>
                      )}

                      {/* USER FIRST MESSAGE TOGGLE */}
                      {currentEdit?.type === 'multiline' && (
                        <HStack width="100%">
                          <ToggleAtom
                            value={currentEdit?.isUserFirstMessage || false}
                            justifyContent="space-between"
                            onChange={value => {
                              if (currentEdit) {
                                setCurrentEdit({ ...currentEdit, isUserFirstMessage: value });
                              }
                            }}
                            label="Map to User's First Message?"
                          />
                        </HStack>
                      )}
                      {/* DEPENDENT TOGGLE */}
                      {!(isStaticContent || isFixedInput) && (
                        <ToggleAtom
                          value={currentEdit?.dependent || false}
                          justifyContent="space-between"
                          onChange={(value: any) => {
                            if (currentEdit) {
                              const newState = value;
                              setCurrentEdit({ ...currentEdit, dependent: newState });

                              if (
                                newState &&
                                currentEdit.type === 'dropdown' &&
                                currentEdit.dependent
                              ) {
                                // 1. Get ALL IDs linked by *other* dropdowns in the form
                                const globallyLinkedIds = getAllLinkedIds(
                                  contactUsFormData,
                                  currentEdit.id,
                                );

                                // 2. Start with the master pool of potential children (excluding q3 and self)
                                const masterDependentPool = allFormDependentFields.filter(
                                  f => f.id !== currentEdit.id,
                                );

                                // 3. Filter the master pool against the global linked pool
                                const unlinkedDependentFields = masterDependentPool.filter(
                                  f => !globallyLinkedIds.includes(f.id),
                                ); // Exclude globally linked

                                setAllDependentFields(unlinkedDependentFields);
                              } else {
                                setAllDependentFields([]);
                              }
                            }
                          }}
                          label="Is this question conditional (dependent)?"
                        />
                      )}
                      {/* DEPENDENCY CONFIGURATION BLOCK */}
                      {currentEdit?.dependent && currentEdit?.type === 'dropdown' && (
                        <Stack
                          spacing={4}
                          p={5}
                          borderWidth="1px"
                          borderColor="purple.500"
                          borderRadius="md"
                          mt={4}
                          bg="purple.50"
                          width="100%" // Ensure dependency block takes full width
                        >
                          <Text fontWeight="bold" fontSize="lg" color="purple.700">
                            Configure Dependent Questions
                          </Text>
                          <VStack align="stretch" spacing={4}>
                            <HStack
                              spacing={4}
                              fontWeight="bold"
                              fontSize="sm"
                              pb={2}
                              borderBottom="1px solid"
                              borderColor="gray.300"
                            >
                              <Box w="30%">Option Label</Box>
                              <Box w="40%">Questions to Link</Box>
                              <Box w="30%">Show If Parent Option</Box>
                            </HStack>
                            {(() => {
                              // Get the list of available options from the CIF data or existing options structure
                              const availableOptions =
                                currentEdit.linked_cif_options ||
                                Object.keys(currentEdit.options || {}).map(label => ({ label }));

                              // 1. Generate the master pool of available dependent questions (IDs/Questions)
                              const masterPool: MultiSelectOption[] = allFormDependentFields.map(
                                f => ({ label: f.question, value: f.id }),
                              );

                              return availableOptions.map(option => {
                                const optionLabel = option.label;
                                const linkedIds = tempOptionsConfig[optionLabel]?.question || [];
                                const showIfValue =
                                  tempOptionsConfig[optionLabel]?.showIf?.join(', ') || '';

                                // 2. Filter the master pool based on the Single Parent Constraint
                                const multiSelectOptions: MultiSelectOption[] = masterPool.filter(
                                  dependentQ => {
                                    const parentId = getDependentQuestionParentId(dependentQ.value);

                                    // Rule 1: Always include the question if it's currently selected for this specific option.
                                    if (linkedIds.includes(dependentQ.value)) {
                                      return true;
                                    }

                                    // Rule 2: If the question has NO parent yet, it's available for linking by currentEdit.
                                    if (!parentId) {
                                      return true;
                                    }

                                    // Rule 3: If the question HAS a parent, only show it if the parent is the card currently being edited.
                                    return parentId === currentEdit.id;
                                  },
                                );

                                return (
                                  <HStack
                                    key={optionLabel}
                                    spacing={4}
                                    alignItems="flex-start"
                                    p={2}
                                    borderBottom="1px solid"
                                    borderColor="gray.100"
                                    _last={{ borderBottom: 'none' }}
                                  >
                                    {/* LEFT COLUMN: Option Label */}
                                    <Box w="30%" pt={1}>
                                      <Text fontWeight="medium">{optionLabel}</Text>
                                    </Box>

                                    {/* RIGHT COLUMN 1: Question to Link (Multi-Select) */}
                                    <Box w="30%">
                                      <DependentQuestionMultiSelect
                                        options={multiSelectOptions}
                                        selectedValues={linkedIds}
                                        onChange={selectedIds =>
                                          handleLinkedQuestionsSelect(optionLabel, selectedIds)
                                        }
                                        placeholder="Link Ques"
                                      />
                                    </Box>

                                    {/* RIGHT COLUMN 2: ShowIf Condition */}
                                    <Box w="40%">
                                      <Input
                                        as="textarea"
                                        placeholder="e.g. Technical Issue"
                                        size="sm"
                                        value={showIfValue}
                                        onChange={e =>
                                          handleShowIfChange(optionLabel, e.target.value)
                                        }
                                        rows={1}
                                        height="50px"
                                        style={{ resize: 'vertical' }}
                                      />
                                    </Box>
                                  </HStack>
                                );
                              });
                            })()}
                          </VStack>
                        </Stack>
                      )}
                    </Stack>
                  ) : (
                    // DISPLAY VIEW MODE
                    <HStack width="100%" justifyContent="space-between" alignItems="flex-start">
                      <Stack spacing={1} width="50%">
                        <HStack alignItems="center">
                          <CustomText fontWeight="bold">{card.question}</CustomText>
                        </HStack>
                        <HStack alignItems="center">
                          <CustomText>{card.description}</CustomText>
                        </HStack>
                        <HStack alignItems="center">
                          <CustomText>{card.placeholder}</CustomText>
                        </HStack>
                      </Stack>
                      {ActionButtons}
                    </HStack>
                  )}
                </Box>
              );
            })}
        </Box>
        <Box mt={6} borderWidth="1px" borderRadius="md" p={4}>
          <CustomText>Upload Contact Us Image</CustomText>
          <HStack spacing={4} mt={2}>
            <Input
              type="file"
              accept="image/png, image/jpg, image/jpeg"
              onChange={handleFileChange}
              ref={fileInputRef}
              bg="white"
            />
            <Button
              colorScheme="blue"
              onClick={handleUpload}
              isDisabled={!selectedFile}
              isLoading={isUploading}
            >
              Upload
            </Button>
          </HStack>
          <Text fontSize="sm" color="gray.500" mt={2}>
            Supported file types: <b>png, jpg, jpeg</b>
          </Text>
          <Text fontSize="sm" color="gray.500">
            Note: This action will immediately upload the selected image.
          </Text>

          {previewUrl && (
            <Box mt={4} display="flex" justifyContent="center">
              <Image
                src={previewUrl}
                alt="Preview"
                boxSize="200px"
                borderRadius="md"
                objectFit="cover"
              />
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
